import type { EChartsOption } from 'echarts';

import type { TimeRange, TimeSeriesChartNavigation } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';

import type { TimeDomain } from '../types/TimeDomain.types';
import { toTimeDomain } from '../utils/timeDomain';

type ChartUpdateSnapshot = {
  timeDomain: TimeDomain;
  hasNavigation: boolean;
  /** Which series and axes exist, in order — not their data. */
  structure: string;
};

type ChartUpdatePolicy = {
  /** ECharts components that must drop entries missing from the new option. */
  replaceMerge: string[];
  shouldResetDataZoomRange: boolean;
};

type OptionComponent = { id?: unknown } | null | undefined;

/**
 * Structural components replace-merge so stale series and axes disappear. Data zoom normally
 * stays merged so its user-controlled viewport survives; it is replaced only across a reset boundary.
 */
const REPLACE_MERGE_COMPONENTS = ['series', 'xAxis', 'yAxis', 'grid'];
const REPLACE_MERGE_COMPONENTS_WITH_DATA_ZOOM = [...REPLACE_MERGE_COMPONENTS, 'dataZoom'];

function getComponentId(component: OptionComponent): string {
  const id = component?.id;

  return typeof id === 'string' || typeof id === 'number' ? String(id) : '';
}

function getComponentIds(components: unknown): string {
  if (!Array.isArray(components)) {
    return '';
  }

  return components.map(getComponentId).join(',');
}

/**
 * Identifies the option's shape: how many series and axes it has and under which ids. Panning and
 * zooming only swap the data inside the same series, which leaves this unchanged.
 */
export function getOptionStructure(option: EChartsOption): string {
  return [getComponentIds(option.series), getComponentIds(option.xAxis), getComponentIds(option.yAxis)].join('|');
}

export function createChartUpdateSnapshot(
  option: EChartsOption,
  timeDomain: TimeRange,
  navigation: TimeSeriesChartNavigation | undefined
): ChartUpdateSnapshot {
  return {
    timeDomain: toTimeDomain(timeDomain),
    hasNavigation: navigation !== undefined,
    structure: getOptionStructure(option),
  };
}

export function areTimeDomainsEqual(previous: TimeDomain, current: TimeDomain): boolean {
  return previous[0] === current[0] && previous[1] === current[1];
}

/**
 * A new domain or a navigation on/off switch is a reset boundary; everything else keeps
 * the zoom. When only the data inside unchanged series moved — which is what a settled pan or zoom
 * does, since the renderer re-thins each line for the new window — nothing is replaced at all:
 * replacing a series rebuilds it from scratch in ECharts and makes it animate in, which reads as
 * the whole chart flickering on every navigation step.
 */
export function getChartUpdatePolicy(previous: ChartUpdateSnapshot, current: ChartUpdateSnapshot): ChartUpdatePolicy {
  const hasTimeDomainChanged = !areTimeDomainsEqual(previous.timeDomain, current.timeDomain);
  const hasNavigationPresenceChanged = previous.hasNavigation !== current.hasNavigation;
  const isResetBoundary = hasTimeDomainChanged || hasNavigationPresenceChanged;
  const hasStructureChanged = previous.structure !== current.structure;

  if (!isResetBoundary && !hasStructureChanged) {
    return { replaceMerge: [], shouldResetDataZoomRange: false };
  }

  return {
    replaceMerge: isResetBoundary ? REPLACE_MERGE_COMPONENTS_WITH_DATA_ZOOM : REPLACE_MERGE_COMPONENTS,
    shouldResetDataZoomRange: current.hasNavigation && isResetBoundary,
  };
}

export function resetDataZoomRange(option: EChartsOption): EChartsOption {
  if (!Array.isArray(option.dataZoom)) {
    return option;
  }

  return {
    ...option,
    dataZoom: option.dataZoom.map((dataZoom) => ({
      ...dataZoom,
      start: 0,
      end: 100,
    })),
  };
}
